import React, { useEffect, useState } from 'react';
import {
  Route, Routes, Navigate, useLocation,
} from 'react-router-dom';

// Pages
import { debounce } from 'lodash';
import Home from './Pages/Home';
import Login from './Pages/Login';
import Orders from './Pages/Orders';
import Events from './Pages/Events';
import NotFound from './Pages/NotFound';
import Partners from './Pages/Partners';

// Services
import { isAuthenticated } from './Controllers/AuthController';

// Components
import Footer from './Components/Footer';
import Header from './Components/Header';
import ScrollMemory from './Components/ScrollMemory';
import ChatWidget from './Components/ChatWidget';
import { AskAiSuggestionProvider } from './Components/ChatWidget/askAiSuggestions';
import MaintenanceScreen from './Components/MaintenanceScreen';

// Stylesheets
import './App.scss';
import Operators from './Pages/Operators';
import Pricing from './Pages/Pricing';
import Trips from './Pages/Trips';
import AcknowledgeNotification from './Pages/AcknowledgeNotification';
import OperatorPortal from './Pages/OperatorPortal';
import Stats from './Pages/Stats';
import PartnerSync from './Pages/PartnerSync';
import AutoApprovalPolicy from './Pages/PartnerSync/AutoApproval/PolicyPage';
import AutoApprovalLog from './Pages/PartnerSync/AutoApproval/DecisionLog';
import AutoApprovalDecision from './Pages/PartnerSync/AutoApproval/DecisionDetail';
import VisitorTracking from './Pages/VisitorTracking';
import Experiences from './Pages/Experiences';
import BookingServices from './Pages/BookingServices';
import NotificationSettings from './Pages/NotificationSettings';
import MessagePresets from './Pages/MessagePresets';
import NotificationGroups from './Pages/NotificationGroups';
import DiscountCodes from './Pages/DiscountCodes';
import ServiceOffers from './Pages/ServiceOffers';
import Payments from './Pages/Payments';
import Invoicing from './Pages/Invoicing';
import Accounts from './Pages/Accounts';
import MonthEnd from './Pages/MonthEnd';
import TripClaims from './Pages/TripClaims';
import CustomerService from './Pages/CustomerService';
import AiAdmin from './Pages/AiAdmin';
import { IngestProvider } from './Components/IngestWorkflow/IngestController';
import UserService from './Services/UserService';
import Permissions from './Utils/Permissions';

// Driver & operator portal — a separate surface: its own token, its own
// guard, its own chrome. It must never render the admin Header/Footer or hit
// the admin session endpoint, so it short-circuits the dashboard shell
// entirely. Its route table lives in PortalRoutes.jsx.
import PortalRoutes, { isPortalPath } from './PortalRoutes';

// Hardcoded bypass — change here, not in the .env — so accidental env
// drift can't grant maintenance bypass to other users.
const MAINTENANCE_BYPASS_EMAIL = 'mark@kravinskiy.com';
const isMaintenanceMode = () => process.env.REACT_APP_MAINTENANCE_MODE === 'true';

function PrivateRoute({
  children, isAuthed, isLoading,
}) {
  if (isLoading) {
    return <div>Loading...</div>;
  }

  return isAuthed ? children : <Navigate to="/login" replace />;
}

function OnlyPublicRoute({
  children, isAuthed, isLoading,
}) {
  if (isLoading) {
    return <div>Loading...</div>;
  }

  return !isAuthed ? children : <Navigate to="/home" replace />;
}

function AppRoutes() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [session, setSession] = useState({});
  const { pathname } = useLocation();
  const onPortal = isPortalPath(pathname);

  const authenticate = async () => {
    try {
      // Replace with your actual async auth check
      const authStatus = await isAuthenticated();
      setIsAuthed(authStatus.success);
      setSession(authStatus.data);

      UserService.getInstance().setModuleAccess(authStatus.data.moduleAccess);
      UserService.getInstance().setUserInfo(authStatus.data);
    } catch (error) {
      // Authentication check failed
      setIsAuthed(false);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedAuth = debounce(() => {
    authenticate();
  }, 500);

  useEffect(() => {
    // A driver has no admin session — asking for one would only fire a 401
    // with somebody else's token semantics attached.
    if (onPortal) return undefined;
    debouncedAuth();
    return () => debouncedAuth.cancel();
  }, [onPortal]);

  // The portal renders before the dashboard shell (and before the maintenance
  // lockout): no Header, no ChatWidget, no Footer, no Bootstrap container, and
  // its own guard instead of PrivateRoute/OnlyPublicRoute.
  if (onPortal) {
    return <PortalRoutes />;
  }

  // Maintenance lockout. Anyone signed in but not the bypass email gets
  // the maintenance screen instead of the app. Unauthenticated users hit
  // the login flow normally so the bypass operator can still sign in.
  const userEmail = (session?.email || '').toLowerCase();
  const inMaintenanceLockout = !isLoading
    && isAuthed
    && isMaintenanceMode()
    && userEmail !== MAINTENANCE_BYPASS_EMAIL.toLowerCase();

  if (inMaintenanceLockout) {
    return <MaintenanceScreen email={session?.email} />;
  }

  return (
    <IngestProvider>
      {/*
        The only wiring the Ask AI launcher's per-page suggestions need: the
        pages that register one and the ChatWidget that reads it both sit under
        here. Adding a page's suggestion never touches this file — see
        Components/ChatWidget/askAiSuggestions.jsx for why it is a push from the
        page rather than a route map.
      */}
      <AskAiSuggestionProvider>
        {/* Going back returns to where the reader was, not to the top. */}
        <ScrollMemory />
        <Header session={session} isAuthed={isAuthed} />
        {isAuthed && <ChatWidget />}
        <div className="container-custom mt-5">
          <Routes>
            <Route
              exact
              path="/home"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Home session={session} />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/login"
              element={(
                <OnlyPublicRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Login setIsAuthed={setIsAuthed} onLoginSuccess={authenticate} />
                </OnlyPublicRoute>
                    )}
            />
            <Route
              exact
              path="/AcknowledgeNotification/:bookingId/:operatorId"
              element={(
                <OnlyPublicRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <AcknowledgeNotification />
                </OnlyPublicRoute>
                    )}
            />
            <Route
              exact
              path="/operator-portal/:operatorId"
              element={(
                <OnlyPublicRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <OperatorPortal />
                </OnlyPublicRoute>
                    )}
            />
            <Route
              exact
              path="/orders"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Orders session={session} />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/events"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Events />
                </PrivateRoute>
                    )}
            />
            {/*
              Customer service correspondence. NO module gate, like /orders: the
              desk is Agent / AgentHead, and the only AI-shaped permission
              (ManageAi) is ADMINISTRATOR-ONLY, so gating on it would hide this
              from the very people it is for. The API gates GET /email-inbox on
              the dashboard authorizer alone for the same reason.

              `ManageAi` EXISTS AND IS HELD — by Administrator, and by nobody
              else. Earlier comments here and in two other files said it was
              "held by nobody" and "absent from the API's accessConfig", which is
              false and had been read as current by three agents in one evening.
              The source is ph-core: `Constants/Permissions.ts:38` defines
              `MANAGE_AI: 'ManageAi'`, and `Helpers/UserAccess.helper.ts:39`
              composes `accessConfig.Administrator` from Agent + AgentHead plus
              the administrator-only permissions, `Permissions.MANAGE_AI` among
              them at :43 — so anything listed there and nowhere else is
              administrator-only by construction. Check those two lines rather
              than trusting this comment.

              The claim was probably once true of a STRING rather than a
              permission: the API renamed `ManageAiKnowledgeBase` to `ManageAi`
              (ph-core commit 1c8943fc), and until both sides deployed, the
              dashboard's constant and the API's accessConfig genuinely named
              different things. It stopped being true and the comment did not.
            */}
            <Route
              exact
              path="/customer-service"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <CustomerService />
                </PrivateRoute>
                    )}
            />
            { UserService.getInstance().canAccessModule('CanInteractWithPartnersScreen') && (
            <Route
              exact
              path="/partners"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Partners />
                </PrivateRoute>
                    )}
            />
            ) }
            { UserService.getInstance().canAccessModule('CanInteractWithOperatorsScreen') && (
            <Route
              exact
              path="/operators"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Operators />
                </PrivateRoute>
                    )}
            />
            )}
            {/* Trip claims: the receipt review's own permission. */}
            { UserService.getInstance().canAccessModule('CanInteractWithOperatorsScreen') && (
            <Route
              exact
              path="/trip-claims"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <TripClaims />
                </PrivateRoute>
                    )}
            />
            )}
            { UserService.getInstance().canAccessModule('CanInteractWithPricingEngine') && (
            <Route
              exact
              path="/pricing"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Pricing />
                </PrivateRoute>
                    )}
            />
            )}
            <Route
              exact
              path="/trips"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Trips />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/stats"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Stats />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/partner-sync"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <PartnerSync />
                </PrivateRoute>
                    )}
            />
            {/*
              Auto-approval is three screens under the sync tab, each with its
              own address so a held offer can be linked to in a message. The
              two static segments are declared BEFORE the :reference route;
              React Router ranks a static segment above a dynamic one whatever
              the order, and the order here is for the reader.

              THERE IS NO `/partner-sync/auto-approval` INDEX. It was a table of
              decisions about the offers already listed on /partner-sync, which
              is the duplication this change removed; each queue card now carries
              its own decision. No redirect is left behind because the page never
              shipped — a redirect from an address nobody holds is residue, and
              the next reader could not tell it from a live path.
            */}
            <Route
              exact
              path="/partner-sync/auto-approval/policy"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <AutoApprovalPolicy />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/partner-sync/auto-approval/log"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <AutoApprovalLog />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/partner-sync/auto-approval/:reference"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <AutoApprovalDecision />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/visitor-tracking"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <VisitorTracking />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/experiences"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Experiences />
                </PrivateRoute>
                    )}
            />
            {UserService.getInstance().canAccessModule('CanManageBookingServices') && (
              <Route
                exact
                path="/booking-services"
                element={(
                  <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                    <BookingServices />
                  </PrivateRoute>
                )}
              />
            )}
            <Route
              exact
              path="/notification-settings"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <NotificationSettings />
                </PrivateRoute>
                    )}
            />
            {/* The office's shared library of saved passenger messages — the
                chips in the passenger-message composer. Ungated, like the
                notification settings beside it: the endpoint is behind the
                dashboard authorizer and has no module of its own. */}
            <Route
              exact
              path="/message-presets"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <MessagePresets />
                </PrivateRoute>
                    )}
            />
            { UserService.getInstance().canAccessModule('CanManageNotificationGroups') && (
            <Route
              exact
              path="/notification-groups"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <NotificationGroups />
                </PrivateRoute>
                    )}
            />
            )}
            { UserService.getInstance().canAccessModule('CanManageDiscountCodes') && (
            <Route
              exact
              path="/discount-codes"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <DiscountCodes />
                </PrivateRoute>
                    )}
            />
            )}
            <Route
              exact
              path="/service-offers"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <ServiceOffers />
                </PrivateRoute>
                    )}
            />
            {UserService.getInstance().canAccessModule('CanInteractWithPayments') && (
            <Route
              exact
              path="/payments"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Payments />
                </PrivateRoute>
                    )}
            />
            )}
            <Route
              exact
              path="/invoicing"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Invoicing />
                </PrivateRoute>
                    )}
            />
            <Route
              exact
              path="/accounts"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Accounts />
                </PrivateRoute>
                    )}
            />
            {/* AI Admin. Configuring the assistant is behind the permission;
                ASKING it a question is NOT — the floating chat is on every page
                for everybody. Only the screens that manage the AI are gated.

                Two shapes, one component: /ai-admin/<area> is each area's own
                deep-linkable address, and bare /ai-admin lands on the first
                area registered in Pages/AiAdmin/areas.js. Adding an AI area
                needs no route here — that is the point of the segment. */}
            {UserService.getInstance().canAccessModule(Permissions.MANAGE_AI) && (
            <>
              <Route
                exact
                path="/ai-admin"
                element={(
                  <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                    <AiAdmin />
                  </PrivateRoute>
                      )}
              />
              <Route
                path="/ai-admin/:area"
                element={(
                  <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                    <AiAdmin />
                  </PrivateRoute>
                      )}
              />
            </>
            )}
            {UserService.getInstance().canAccessModule('CanInteractWithMonthEnd') && (
            <Route
              exact
              path="/month-end"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <MonthEnd />
                </PrivateRoute>
                    )}
            />
            )}
            <Route
              path="/"
              element={(
                <PrivateRoute isAuthed={isAuthed} isLoading={isLoading}>
                  <Home session={session} />
                </PrivateRoute>
                    )}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        <Footer />
      </AskAiSuggestionProvider>
    </IngestProvider>
  );
}

export default AppRoutes;
